import { DimensionSide, LineType, Polyline } from '@ritabygglov/core';
import { useRef } from 'react';
import { useIntl } from 'react-intl';
import { createDimensionId } from '../../../helpers/dimensionIds';
import {
  calculateAngle,
  getDistance,
  offsetLine,
  pairNeighbors,
  radiansToDegrees,
} from '../../../helpers/utility';
import { useMeasurement } from '../../../hooks/useMeasurement';
import { DimensionLine } from './components/DimensionLine';

type PolylineSegmentDimensionProps = {
  element: Polyline;
  segmentIndex: number;
  scalingFactor: number;
  scale: number;
  lineType?: LineType;
  onToggleDimensionSide: (pointIndex: number) => void;
  draggingDimensionId: string | null;
};

const PolylineSegmentDimension = ({
  element,
  segmentIndex,
  scalingFactor,
  scale,
  lineType,
  onToggleDimensionSide,
  draggingDimensionId,
}: PolylineSegmentDimensionProps) => {
  const intl = useIntl();
  const { formatLength } = useMeasurement();
  const mouseDownPosRef = useRef<{ x: number; y: number } | null>(null);
  const defaultDistance = lineType === LineType.Dimension ? 5 : 15;
  const s = element.points[segmentIndex];
  const e = element.points[segmentIndex + 1];

  if (!s || !e) return null;

  const startPoint =
    s.connectingPointIndex !== undefined
      ? element.points[s.connectingPointIndex]
      : s;
  const endPoint =
    e.connectingPointIndex !== undefined
      ? element.points[e.connectingPointIndex]
      : e;

  const distance = s.dimensionOffset ?? defaultDistance;

  const [start, end, text] = offsetLine(
    { x: startPoint.x, y: startPoint.y },
    { x: endPoint.x, y: endPoint.y },
    distance,
    s.dimensionSide
  );

  const angle = radiansToDegrees(
    calculateAngle(
      { x: startPoint.x, y: startPoint.y },
      { x: endPoint.x, y: endPoint.y }
    )
  );

  const showExtensionLines = lineType === LineType.Line;
  const segmentDimensionId = createDimensionId(
    element.id,
    'segment',
    segmentIndex
  );
  const isSegmentDragging = draggingDimensionId === segmentDimensionId;

  const segmentDistance = getDistance(
    { x: startPoint.x, y: startPoint.y },
    { x: endPoint.x, y: endPoint.y }
  );

  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownPosRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleClick = (e: React.MouseEvent) => {
    if (!mouseDownPosRef.current) {
      onToggleDimensionSide(segmentIndex);
      return;
    }

    const dx = Math.abs(e.clientX - mouseDownPosRef.current.x);
    const dy = Math.abs(e.clientY - mouseDownPosRef.current.y);
    const CLICK_THRESHOLD = 1; // pixels

    // Only toggle if mouse didn't move (was a click, not a drag)
    if (dx <= CLICK_THRESHOLD && dy <= CLICK_THRESHOLD) {
      onToggleDimensionSide(segmentIndex);
    }

    mouseDownPosRef.current = null;
  };

  const fontSize = element.style?.fontSize || '14px';
  const fontWeight = element.style?.fontWeight || '400';

  return (
    <DimensionLine
      scale={scale}
      id={segmentDimensionId}
      lineStart={start}
      lineEnd={end}
      cornerStart={
        showExtensionLines ? [startPoint.x, startPoint.y] : undefined
      }
      cornerEnd={showExtensionLines ? [endPoint.x, endPoint.y] : undefined}
      showExtensionLines={showExtensionLines}
      isDragging={isSegmentDragging}
    >
      <title id="unique-id">
        {intl.formatMessage({ id: 'polylineDimensions.flipSide' })}
      </title>
      <text
        className="dimension-text"
        style={{
          cursor: 'move',
          fontSize,
          fontWeight,
        }}
        transform={`translate(${text[0]}, ${text[1]}) rotate(${
          s.dimensionSide === DimensionSide.Above ? angle + 180 : angle
        })`}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
      >
        {formatLength(segmentDistance * scalingFactor)}
      </text>
    </DimensionLine>
  );
};

type PolylineDimensionsProps = {
  element: Polyline;
  scalingFactor: number;
  scale: number;
  lineType?: LineType;
  onToggleDimensionSide: (pointIndex: number) => void;
  draggingDimensionId: string | null;
};

export const PolylineDimensions = ({
  element,
  scalingFactor,
  scale,
  lineType,
  onToggleDimensionSide,
  draggingDimensionId,
}: PolylineDimensionsProps) => {
  const segments = pairNeighbors(element.points);

  return (
    <g>
      {segments.map((_, index) => (
        <PolylineSegmentDimension
          key={`${element.id}-segment-${index}`}
          element={element}
          segmentIndex={index}
          scalingFactor={scalingFactor}
          scale={scale}
          lineType={lineType}
          onToggleDimensionSide={onToggleDimensionSide}
          draggingDimensionId={draggingDimensionId}
        />
      ))}
    </g>
  );
};
